
// Privacy.js

import React from 'react';

import LegalHeader from '../components/legalcomponents/LegalHeader';
import LegalContent from '../components/legalcomponents/LegalContent';

// The document itself lives here, not inside LegalContent — LegalContent
// is just the generic "table of contents + sections" template. Each
// section needs a unique `id` (used for the bookmark link + scroll-spy),
// a `title` (shown in both the bookmark list and as the section heading),
// and `content` (any JSX — usually one or more <p> tags).
const sections = [
    {
        id: 'introduction',
        title: 'I. Introduction',
        content: (
            <>
                <p>
                    At NicheWare, we take the privacy of personal information seriously. 
                    This Privacy Policy explains how NicheWare collects, uses, discloses, 
                    and protects personal information when you visit our website or contact us through our website.
                </p>
                <p>
                    In this Privacy Policy, "NicheWare," "we," "our," and "us" refer to NicheWare,
                    the operator of this website.
                </p>
                <p>
                    This Privacy Policy applies to our public website and website contact form. 
                    It does not necessarily apply to personal information processed through software products that 
                    NicheWare provides to its clients, which may be subject to separate agreements or privacy practices.
                </p>
            </>
        ),
    },
    {
        id: 'information-we-collect',
        title: 'II. Information We Collect',
        content: (
             <>
                <p>
                    When you choose to contact us through our website, we collect the information you provide, 
                    including your first and last name, work email address, company name, telephone number, 
                    and the contents of your message.
                </p>
                <p>
                    Our website also uses security technologies, including Google reCAPTCHA Enterprise, 
                    that may process technical and interaction information such as IP address, device or browser information, 
                    and information about how you interact with the website for the purpose of detecting spam, fraud, 
                    and automated submissions.
                </p>
                <p>
                    We do not intentionally collect sensitive personal information through our contact form, 
                    and we ask that you do not include sensitive personal information unless it is necessary for your inquiry.
                </p>
            </>
        ),
    },
    {
        id: 'how-we-use-your-information',
        title: 'III. How We Use Your Information',
        content: (
            <>
                <p>
                   We use personal information collected through our website to:
                </p>

                <ul>
                    <li>respond to questions and business inquiries;</li>
                    <li>understand the organization and project needs described in an inquiry;</li>
                    <li>communicate with prospective clients about potential services or projects;</li>
                    <li>protect our website against spam, abuse, fraud, and other security threats; and</li>
                    <li>comply with applicable legal or regulatory requirements where necessary.</li>
                </ul>

                <p>
                    We do not use information submitted through the contact form to automatically 
                    enroll you in a marketing mailing list.
                </p>
            </>            
        ),
    },
    {
        id: 'contact-form-submissions',
        title: 'IV. Contact Form Submissions',
        content: (
            <>
                <p>
                    When you submit our contact form, the information you provide is transmitted to our server infrastructure 
                    so that your inquiry can be delivered to the NicheWare team by email.
                </p>

                <p>
                    NicheWare does not maintain a separate database of contact-form submissions. 
                    Our contact-form backend is hosted using Google Firebase, and we use Resend as an 
                    email-delivery provider to transmit the submitted information to our email system.    
                </p>
                <p>
                    The resulting email correspondence may remain in our email system for as long as reasonably necessary
                    to review and respond to your inquiry and for legitimate business, security, or legal purposes.
                </p>
                <p>
                    We also use Google reCAPTCHA Enterprise to help identify and prevent spam, abuse, and automated submissions.
                </p>
            </>
            
        ),
    },
    {
        id: 'service-providers',
        title: 'V. Service Providers & International Data Transfers',
        content: (
            <>
                <p>
                    We use third-party service providers to operate our website and process contact-form submissions. 
                    These providers process information only as necessary to provide the services we use.
                </p>

                <p>
                    Our current service providers include:
                </p>
                
                <p>
                    Google Firebase — hosts the backend used to process contact-form submissions.
                </p>

                <p>
                    Google reCAPTCHA Enterprise — helps protect our website and contact form against spam, fraud, and automated abuse.
                </p>

                <p>
                    Resend — provides email-delivery infrastructure used to deliver contact-form submissions to NicheWare.
                </p>
                
                <p>
                    Information submitted through our contact form may be processed or stored in the United States. 
                    As a result, the information may be subject to the laws of the United States and may be accessible to courts, 
                    law-enforcement agencies, or other governmental authorities where permitted by applicable law.
                </p>
                
                <p>
                    For information about NicheWare's use of service providers outside Canada,
                     you may contact our Privacy Officer using the contact information provided at the end of this Policy.
                </p>
                
            </>
        ),
    },
    {
        id: 'cookies-and-tracking',
        title: 'VI. Cookies and Tracking Technologies',
        content: (
            <>
                <p>
                    Our website uses technologies necessary to operate and protect the website, 
                    including Google reCAPTCHA Enterprise. These technologies may use cookies or 
                    process technical and interaction information to help distinguish legitimate users from automated or abusive traffic.
                </p>

                <p>
                    We currently do not use analytics or advertising cookies to track visitors for marketing purposes.
                </p>
                
                <p>
                    You can control or delete cookies through your browser settings, 
                    although disabling certain technologies may affect the operation or security of parts of the website.
                </p>
            </>
            
        ),
    },
    {
        id: 'data-retention',
        title: 'VII. Data Retention',
        content: (
            <>
                <p>
                    NicheWare does not maintain a separate database of website contact-form submissions.
                </p>
                <p>
                    Information processed by service providers may be temporarily retained in their systems in accordance
                    with their applicable retention practices. The email generated from your submission may also remain 
                    in NicheWare's email system while reasonably necessary to respond to the inquiry, maintain appropriate 
                    business records, resolve disputes, or satisfy legal or security requirements.
                </p>
                <p>
                    When personal information is no longer reasonably required for a business or legal purpose, 
                    we take reasonable steps to delete it or render it non-identifying.
                </p>
            </>
 
        ),
    },
    {
        id: 'how-we-protect-your-information',
        title: 'VIII. How We Protect Your Information',
        content: (
            <>
                <p>
                    We use reasonable administrative, technical, and organizational safeguards designed to protect personal information
                    against unauthorized access, collection, use, disclosure, modification, loss, or destruction.
                </p>
                <p>
                    Access to personal information is limited to individuals and service providers who reasonably require it 
                    for the purposes described in this Privacy Policy.
                </p>
                <p>
                    No method of transmitting or storing information is completely secure, and we cannot guarantee absolute security.
                </p>
            </>
            
        ),
    },
    {
        id: 'third-party-websites',
        title: 'IX. Third-Party Websites',
        content: (
            <p>
                Our website may contain links to websites operated by clients, partners, or other third parties. 
                NicheWare does not control those websites and is not responsible for their privacy practices or content. 
                We encourage you to review the privacy policies of third-party websites before providing personal information to them.
            </p>
        ),
    },
    {
        id: 'your-rights-and-choices',
        title: 'X. Your Rights and Choices',
        content: (
            <>
                <p>
                    Depending on where you are located, applicable privacy law may give you some or all of the
                    following rights with respect to the personal information we hold about you:
                </p>
                <ul>
                    <li><strong>Access</strong> — request access to the personal information we hold about you;</li>
                    <li><strong>Correction</strong> — request correction of information that is inaccurate or incomplete;</li>
                    <li><strong>Deletion</strong> — request deletion of personal information that is no longer necessary for the purposes it was collected;</li>
                    <li><strong>Restriction</strong> — request that we restrict certain processing of your personal information;</li>
                    <li><strong>Withdrawal of consent</strong> — withdraw consent to a particular use of your personal information at any time, without affecting the lawfulness of processing carried out before withdrawal; and</li>
                    <li><strong>Complaints</strong> — lodge a complaint with the applicable privacy regulator, such as the Office of the Privacy Commissioner of Canada, if you believe our handling of your personal information does not comply with applicable law.</li>
                </ul>
                <p>
                    Requests relating to access, correction, deletion, or other privacy concerns should be submitted in writing
                    to our Privacy Officer using the contact information below. We may take reasonable steps
                    to verify your identity before responding to a request, and rights may be subject to legal or contractual limitations.
                </p>
            </>

        ),
    },
    {
        id: 'changes-to-this-policy',
        title: 'XI. Changes to This Policy',
        content: (
            <>
                <p>
                    We may update this Privacy Policy periodically to reflect changes to our website, service providers,
                    business practices, or legal requirements. When we make changes, we will update 
                    the "Last Updated" date displayed at the top of this page.
                </p>
                <p>
                    If we make material changes to how we handle personal information, 
                    we may provide additional notice where appropriate or required by law.
                </p>
            </>
           
        ),
    },
    {
        id: 'contact-us-about-privacy',
        title: 'XII. Contact Us About Privacy',
        content: (
            <>
                <p>
                    If you have questions about this Privacy Policy or how we handle your
                    information, please contact us at{' '}
                    <a href="mailto:jeannicolasrouette@nicheware.ca">jeannicolasrouette@nicheware.ca</a>{' '}
                    or through our <a href="/contact">contact page</a>.
                </p>
            </>
            
        ),
    },
];

const Privacy = () => {

    return (
        <>
            <LegalHeader title="Privacy Policy" lastUpdated="September 2026."/>

            <LegalContent sections={sections}/>
        </>

    )
}

export default Privacy;
